// authApi.ts - Enhanced authentication API
import { apiCall } from './api';
import type { LoginData, RegisterData, AuthResponse, UserProfile, RefreshTokenResponse } from '@/types';
import type { ApiResponse } from '@/types/api';
import { userService } from './userService';

// Mutex to prevent concurrent refresh token attempts
let refreshTokenPromise: Promise<ApiResponse<RefreshTokenResponse>> | null = null;

// Mock data for authentication
const mockAuthResponse: AuthResponse = {
  user: {
    id: 1,
    first_name: 'John Doe',
    last_name: 'John Doe',
    name: 'John Doe',
    email: 'john@example.com',
    email_verified_at: '2024-01-15T10:00:00Z',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  access_token: 'mock_jwt_access_token_12345',
  refresh_token: 'mock_jwt_refresh_token_12345',
  token_type: 'Bearer',
  expires_in: 3600
};

const mockUserProfile: UserProfile = {
  id: 1,
  first_name: 'John Doe',
  last_name: 'John Doe',
  name: 'John Doe',
  email: 'john@example.com',
  avatar: '/images/avatars/user1.jpg',
  bio: 'Passionate developer and tech enthusiast',
  location: 'New York, USA',
  website: 'https://johndoe.dev',
  email_verified_at: '2024-01-15T10:00:00Z',
  created_at: '2024-01-01T00:00:00Z',
  updated_at: '2024-01-15T10:00:00Z'
};

export const authApi = {
  // Login user
  login: async (credentials: LoginData): Promise<ApiResponse<AuthResponse>> => {
    return apiCall<AuthResponse>('/auth/login', {
      method: 'POST',
      data: credentials,
      mockData: mockAuthResponse
    });
  },

  // Register new user
  register: async (userData: RegisterData): Promise<ApiResponse<AuthResponse>> => {
    return apiCall<AuthResponse>('/auth/register', {
      method: 'POST',
      data: userData,
      mockData: mockAuthResponse
    });
  },

  // Get current user profile
  getCurrentUser: async (): Promise<ApiResponse<UserProfile>> => {
    return apiCall<UserProfile>('/user/profile', {
      mockData: mockUserProfile
    });
  },

  // Update user profile
  updateProfile: async (data: Partial<UserProfile>): Promise<ApiResponse<UserProfile>> => {
    return apiCall<UserProfile>('/user/profile', {
      method: 'PUT',
      data,
      mockData: { ...mockUserProfile, ...data }
    });
  },

  // Refresh token
  refreshToken: async (): Promise<ApiResponse<RefreshTokenResponse>> => {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      console.error('No refresh token available in localStorage');
      throw new Error('No refresh token available');
    }

    // Validate refresh token format (should be a hash string, typically 64 characters)
    if (typeof refreshToken !== 'string' || refreshToken.trim().length === 0) {
      console.error('Invalid refresh token format:', refreshToken);
      userService.clearTokens();
      throw new Error('Invalid refresh token format');
    }

    // Check refresh token age - if it's too old, don't try to refresh
    const refreshTokenTimestamp = localStorage.getItem('refresh_token_timestamp');
    const REFRESH_TOKEN_MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days in ms
    
    if (refreshTokenTimestamp) {
      const refreshTokenAge = Date.now() - parseInt(refreshTokenTimestamp);
      if (refreshTokenAge > REFRESH_TOKEN_MAX_AGE) {
        console.error('Refresh token too old, requiring re-login');
        userService.clearTokens();
        throw new Error('Refresh token expired');
      }
    }

    // If there's already a refresh in progress, wait for it
    if (refreshTokenPromise) {
      console.log('Refresh token already in progress, waiting for existing request...');
      return refreshTokenPromise;
    }

    // Check if we've recently tried to refresh
    const lastRefreshAttempt = localStorage.getItem('last_refresh_attempt');
    const REFRESH_COOLDOWN = 2000; // 2 seconds (reduced from 5 to allow faster retries)
    
    if (lastRefreshAttempt && Date.now() - parseInt(lastRefreshAttempt) < REFRESH_COOLDOWN) {
      // If cooldown is active, wait for it to expire, then retry
      const waitTime = REFRESH_COOLDOWN - (Date.now() - parseInt(lastRefreshAttempt));
      await new Promise(resolve => setTimeout(resolve, waitTime));
      // After waiting, check if another refresh happened
      if (refreshTokenPromise) {
        return refreshTokenPromise;
      }
    }

    // Store attempt timestamp
    localStorage.setItem('last_refresh_attempt', Date.now().toString());

    // Create the refresh promise and store it
    refreshTokenPromise = (async () => {
      try {
        console.log('Attempting to refresh token...');
        const response = await apiCall<RefreshTokenResponse>('/auth/refresh', {
          method: 'POST',
          data: { refresh_token: refreshToken },
          mockData: {} as any
        });
        
        // Update tokens on successful refresh
        console.log('Refresh token response:', response);
        
        // Handle backend response structure: { status: 'success', data: { access_token, ... } }
        // apiCall returns: { success: true, data: { status: 'success', data: { access_token, ... } } }
        let refreshData: RefreshTokenResponse | null = null;
        
        if (response.success && response.data) {
          // Check if response.data has nested structure from backend
          if ((response.data as any).status === 'success' && (response.data as any).data) {
            // Backend wrapped response: { status: 'success', data: { ... } }
            refreshData = (response.data as any).data as RefreshTokenResponse;
          } else if ((response.data as any).access_token) {
            // Direct response: { access_token, ... }
            refreshData = response.data as RefreshTokenResponse;
          }
        }
        
        if (refreshData && refreshData.access_token) {
          console.log('Token refresh successful, updating tokens', {
            hasAccessToken: !!refreshData.access_token,
            hasRefreshToken: !!refreshData.refresh_token,
            expiresIn: refreshData.expires_in
          });
          // Convert RefreshTokenResponse to AuthResponse format for updateTokens
          const authResponse = {
            access_token: refreshData.access_token,
            refresh_token: refreshData.refresh_token || localStorage.getItem('refresh_token') || '',
            token_type: refreshData.token_type || 'Bearer',
            expires_in: refreshData.expires_in || 1800, // Default 30 minutes
          };
          userService.updateTokens(authResponse);
          // Clear the attempt timestamp on success
          localStorage.removeItem('last_refresh_attempt');
        } else {
          console.error('Token refresh response missing access_token:', {
            response,
            responseData: response.data,
            nestedData: (response.data as any)?.data,
            hasAccessToken: !!refreshData?.access_token
          });
          throw new Error('Refresh token response missing access_token');
        }
        
        return response;
      } catch (error: any) {
        console.error('Token refresh failed:', {
          status: error?.response?.status,
          message: error?.message,
          data: error?.response?.data
        });
        
        // If refresh token is invalid (401), clear it to prevent loops
        if (error?.response?.status === 401) {
          const errorMessage = error?.response?.data?.message || error?.response?.data?.error || 'Invalid refresh token';
          console.error('Refresh token invalid (401):', errorMessage);
          userService.clearTokens();
        }
        throw error;
      } finally {
        // Clear the promise so future calls can refresh again
        refreshTokenPromise = null;
      }
    })();

    return refreshTokenPromise;
  },

  // Logout
  logout: async (): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>('/auth/logout', {
      method: 'POST',
      mockData: { message: 'Successfully logged out' }
    });
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      data: { email },
      mockData: { message: 'Password reset link sent to your email' }
    });
  },

  // Reset password
  resetPassword: async (data: { token: string; email: string; password: string; password_confirmation: string }): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      data,
      mockData: { message: 'Password successfully reset' }
    });
  }
};